import { useProcedureAssignmentSettingRuleSet as useCommonHook } from "../common/hooks";
import { AssignmentSettingRuleSetInput } from "../../../../../application/domain/procedure-assignment-setting-ruleset/procedure-assignment-setting-ruleset";
import apiClient from "../../../../../application/apis/api-client";
import { reportUnexpectedError } from "@graffer-inc/smart-apply";
import { toast } from "react-toastify";
import { SmartApplyR } from "../../route";
import { useParams } from "react-router-dom";
import { useEffect, useCallback } from "react";

interface RouteParams {
  id: string;
}

export const useProcedureAssignmentSettingRuleSetEdit = () => {
  const hookResult = useCommonHook();
  const { id } = useParams<RouteParams>();
  const {
    rulesetName,
    description,
    targetFields,
    rules,
    validateFormData,
    auth,
    setIsLoading,
    history,
    setRulesetName,
    setDescription,
    setTargetFields,
    setRules,
    fetchGroupMaps,
  } = hookResult;

  const fetchRuleset = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const response = await apiClient.getProcedureAssignmentSettingRuleSet(id);
      const ruleset = response.data;

      setRulesetName(ruleset.name);
      setDescription(ruleset.description);
      setTargetFields(ruleset.targetFields);
      setRules(ruleset.rules);

      // ルールセットのデータ取得後、明示的にグループ情報をフェッチ
      await fetchGroupMaps(ruleset.rules, auth.orgId);
    } catch (error) {
      reportUnexpectedError(error);
      toast.error("申請振り分け設定の取得に失敗しました。");
      history.push(SmartApplyR.AssignmentSettingRulesets.link({}));
    } finally {
      setIsLoading(false);
    }
    // idのみを依存配列に含め、セッター関数は除外して無限ループを防止する
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchRuleset();
  }, [fetchRuleset]);

  const handleSave = async () => {
    const isValid = validateFormData();

    if (!isValid) {
      return;
    }

    const input: AssignmentSettingRuleSetInput = {
      tenantId: auth.orgId,
      name: rulesetName,
      description,
      targetFields,
      rules,
    };

    setIsLoading(true);
    try {
      await apiClient.updateProcedureAssignmentSettingRuleSet(id, input);
      toast.success("申請振り分け設定が更新されました。");
      history.push(SmartApplyR.AssignmentSettingRulesets.link({}));
    } catch (error) {
      reportUnexpectedError(error);
      toast.error("申請振り分け設定の更新に失敗しました。");
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      await apiClient.archiveProcedureAssignmentSettingRuleSet(id);
      toast.success("申請振り分け設定がアーカイブされました。");
      history.push(SmartApplyR.AssignmentSettingRulesets.link({}));
    } catch (error) {
      reportUnexpectedError(error);
      toast.error("申請振り分け設定のアーカイブに失敗しました。");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    ...hookResult,
    id,
    handleSave,
    handleArchive,
  };
};
